import { CorridorType, FilterOperator } from '@kinetic-ui/data-layer';
import {
  AppSearchIconKey,
  AppSearchItemType,
  DEFAULT_ZOOM,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useCorridorMapListData,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
} from '@kinetic-ui/shared';
import { AddRoadOutlined } from '@mui/icons-material';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/const';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { hasEntityDrawer } = useContext(MapContext);
  const { searchText } = useMapBoxSearchContext();
  const { push } = useHistory();
  const map = useKineticMap();
  const { addSearchHistory } = useAppSearchHistory();

  const filters = [
    {
      operator: FilterOperator.Equals,
      value: CorridorType.Corridor,
      field: 'type',
    },
  ];

  const { rowData } = useCorridorMapListData({
    activeFilters: filters,
    searchText,
    skip: !searchText,
  });

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const corridor = rowData.find((e) => e.id === id);
        if (corridor?.name) {
          addSearchHistory({
            name: corridor.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Corridor,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [addMapPin, addSearchHistory, hasEntityDrawer, isAppSearch, map, onResultClick, push, rowData]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {rowData.map((corridor) => (
        <MapSearchResultItem
          key={`searchItem_${corridor.id}`}
          name={corridor.name}
          onClick={(openInNewWindow) => onClick(corridor.id, corridor.geometry, openInNewWindow)}
          icon={<AddRoadOutlined />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
